import { MenuPopoverProps } from "../../types/Popover";
import { Menu, Stack, Typography } from "@mui/material";
import { useTheme } from "styled-components";
import {
  CreateBoardIcon,
  CreateFolderIcon,
  CreateNewIcon,
  UploadIcon,
} from "../../assets/Icons";
import SaveNewAdModal from "../modals/saveNewAdModal";
import CreateFolderModal from "../modals/createFolderModal";
import { useDispatch, useSelector } from "react-redux";
import {
  modalTabIndexSelector,
  openCreateFolderSelector,
  openCreateTeamFolderSelector,
  openSaveNewAdSelector,
} from "../../store/metadata/selector";
import { StyledDivider } from "../../globalStyle";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import CreateTeamFolderModal from "../modals/createTeamFolderModal";
import { activeSubscriptionSelector } from "../../store/payment/selector";
import {
  getPlanAccess,
  isFolderOverLimit,
  isBoardOverLimit,
} from "../../utils/planAccess";
import { folderListsSelector } from "../../store/metadata/selector";
import { userDetailsSelector } from "../../store/account/selector";

const ItemRow = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <Stack
    direction="row"
    sx={{ alignItems: "center", gap: "0.625rem", cursor: "pointer" }}
    onClick={onClick}
  >
    {children}
  </Stack>
);

const StyledText = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <Typography
      fontSize="0.938rem"
      fontWeight="500"
      color={theme.colors.black2}
    >
      {children}
    </Typography>
  );
};

const CustomDivider = () => (
  <StyledDivider sx={{ marginInline: "-0.938rem" }} />
);

const CreateNewPopover = ({
  anchorEl,
  handlePopoverClose,
}: MenuPopoverProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const activePlan = useSelector(activeSubscriptionSelector);
  const userDetails = useSelector(userDetailsSelector);
  const folderListToShow = useSelector(folderListsSelector);
  const openCreateFolder = useSelector(openCreateFolderSelector);
  const openCreateTeamFolder = useSelector(openCreateTeamFolderSelector);
  const openSaveNewAd = useSelector(openSaveNewAdSelector);
  const modalIndex = useSelector(modalTabIndexSelector);

  // Get proper access config based on parent/child
  const accessConfig = getPlanAccess(
    activePlan?.activePlanDetails?.planType ||
      userDetails?.parentPlanDetails?.planType
  );

  const folderOverLimit = isFolderOverLimit(
    folderListToShow?.userFolders?.length ?? 0,
    accessConfig
  );
  const totalBoardsCount =
    folderListToShow?.userFolders?.reduce(
      (count: number, folder: any) => count + (folder.boards?.length || 0),
      0
    ) ?? 0;

  const handleSaveNewAdModal = (index: number) => {
    dispatch(MetaDataReducerAction.setModalTabIndex(index));
    dispatch(MetaDataReducerAction.setOpenSaveNewAd(true));
    handlePopoverClose();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handlePopoverClose}
        variant="selectedMenu"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          ".MuiMenu-paper": {
            borderRadius: "0.938rem",
            padding: "0.938rem",
            width: "13.875rem",
          },
          ".MuiList-root": {
            display: "flex",
            flexDirection: "column",
            gap: "0.938rem",
            padding: "0.5rem 0",
          },
        }}
      >
        <ItemRow onClick={() => handleSaveNewAdModal(0)}>
          <CreateNewIcon fill={theme.colors.black2} />
          <StyledText>Save New Ad</StyledText>
        </ItemRow>
        <CustomDivider />
        <ItemRow onClick={() => handleSaveNewAdModal(1)}>
          <UploadIcon color={theme.colors.black2} />
          <StyledText>Bulk Upload</StyledText>
        </ItemRow>
        <CustomDivider />
        <ItemRow
          onClick={() => {
            if (isBoardOverLimit(totalBoardsCount, 0, accessConfig)) {
              dispatch(MetaDataReducerAction.setOpenUpgradePlanModal(true));
            } else {
              dispatch(
                MetaDataReducerAction.setOpenBoardModal({
                  isOpen: true,
                  folderId: activePlan?.defaultFolderId || null,
                })
              );
              handlePopoverClose();
            }
          }}
        >
          <CreateBoardIcon />
          <StyledText>Create Board</StyledText>
        </ItemRow>
        <CustomDivider />
        <ItemRow
          onClick={() => {
            if (folderOverLimit) {
              dispatch(MetaDataReducerAction.setOpenUpgradePlanModal(true));
            } else {
              dispatch(MetaDataReducerAction.setOpenCreateFolder(true));
              handlePopoverClose();
            }
          }}
        >
          <CreateFolderIcon fill="#414042" />
          <StyledText>Create Folder</StyledText>
        </ItemRow>
        <CustomDivider />
        <ItemRow
          onClick={() => {
            if (accessConfig.planType === "Free") {
              // Free plan users cannot create team folders
              dispatch(MetaDataReducerAction.setOpenUpgradePlanModal(true));
            } else {
              dispatch(MetaDataReducerAction.setOpenTeamCreateFolder(true));
              handlePopoverClose();
            }
          }}
        >
          <CreateFolderIcon fill="#414042" />
          <StyledText>Create Team Folder</StyledText>
        </ItemRow>
      </Menu>

      {openSaveNewAd && (modalIndex === 0 || modalIndex === 1) && (
        <SaveNewAdModal modalTabIndex={modalIndex} />
      )}
      {openCreateFolder && <CreateFolderModal />}
      {openCreateTeamFolder && <CreateTeamFolderModal />}
    </>
  );
};

export default CreateNewPopover;
